import { ConcelhoRecord, NationalSummary } from '@utils/eclipse2026';

/**
 * O ficheiro .ics do botão "Adicionar ao calendário", montado no browser e servido
 * como blob — sem endpoint, sem pedido de rede.
 *
 * `displayEnd` e não `partialEnd` para a hora de fim, pela mesma razão do painel: em
 * 34 concelhos do continente (todo o Algarve) o Sol põe-se antes do fim astronómico,
 * e um evento de calendário que se prolongue para depois do pôr-do-sol está a marcar
 * tempo em que já não há nada para ver.
 */

/** "2026-08-12T18:33:54.400Z" -> "20260812T183354Z" (formato UTC do RFC 5545). */
function icsStamp(utcIso: string): string {
    return new Date(utcIso)
        .toISOString()
        .replace(/[-:]/g, '')
        .replace(/\.\d{3}/, '');
}

/** RFC 5545: quebras CRLF, e escapar `\` `;` `,` e newlines no texto. */
function escapeText(text: string): string {
    return text.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');
}

export interface CalendarEvent {
    /** Início/fim em UTC ISO. */
    startUtc: string;
    endUtc: string;
    summary: string;
    description: string;
    location: string;
}

export function eventForConcelho(c: ConcelhoRecord, articleUrl: string): CalendarEvent {
    const pct = c.obscurationLabel;
    return {
        startUtc: c.partialBegin.utc,
        endUtc: c.displayEnd.utc,
        summary: `Eclipse solar em ${c.nome} — ${pct} do Sol tapado`,
        description: [`Início ${c.partialBegin.label}, máximo ${c.peak.label}, fim ${c.displayEnd.label} (hora local).`, `No máximo o Sol estará a cerca de ${Math.round(c.peak.sunAltitude ?? 0)}° acima do horizonte.`, 'Use sempre óculos com certificação ISO 12312-2.', articleUrl].join('\n'),
        location: c.nome,
    };
}

export function eventForCountry(national: NationalSummary, articleUrl: string): CalendarEvent {
    return {
        startUtc: national.partialBegin.firstUtc,
        endUtc: national.partialEnd.lastUtc,
        summary: 'Eclipse solar em Portugal',
        description: [`Entre ${national.obscurationPct.minLabel} e ${national.obscurationPct.maxLabel} do Sol tapado, com o máximo entre as ${national.peak.firstLabel} e as ${national.peak.lastLabel}.`, 'Escolha o seu concelho no mapa para as horas exactas.', 'Use sempre óculos com certificação ISO 12312-2.', articleUrl].join('\n'),
        location: 'Portugal',
    };
}

export function buildIcs(event: CalendarEvent): string {
    // UID estável por evento: adicionar duas vezes substitui em vez de duplicar na
    // maioria dos clientes de calendário.
    const uid = `eclipse-2026-08-12-${event.location.toLowerCase().replace(/[^a-z0-9]+/g, '-')}@observador.pt`;
    return [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//Observador//Eclipse 2026//PT',
        'CALSCALE:GREGORIAN',
        'BEGIN:VEVENT',
        `UID:${uid}`,
        `DTSTAMP:${icsStamp(event.startUtc)}`,
        `DTSTART:${icsStamp(event.startUtc)}`,
        `DTEND:${icsStamp(event.endUtc)}`,
        `SUMMARY:${escapeText(event.summary)}`,
        `DESCRIPTION:${escapeText(event.description)}`,
        `LOCATION:${escapeText(event.location)}`,
        'END:VEVENT',
        'END:VCALENDAR',
    ].join('\r\n');
}

/** Dispara o download. Revoga o URL do blob a seguir — sem isso fica um objecto
 *  pendurado na memória por cada clique. */
export function downloadIcs(event: CalendarEvent): void {
    const blob = new Blob([buildIcs(event)], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'eclipse-solar-2026.ics';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

/**
 * "7d 06h 12m" / "4h 12m" / "12m".
 *
 * Os minutos aparecem também no formato com dias: sem eles a contagem ficava parada
 * uma hora inteira, e a única coisa que um contador tem de fazer é parecer que está
 * a contar. Não obriga a ticar mais depressa — o `CalendarBlock` já actualiza ao
 * minuto, que é exactamente a granularidade mostrada.
 *
 * Deliberadamente separado do `coarseDuration` de `banner-eclipse-2026/state.ts`: o
 * banner tica ao segundo e precisa de uma variante fina; aqui a contagem nunca mostra
 * segundos. Partilhar obrigaria a exportar duas funções de uma máquina de estados que
 * o interactivo não usa.
 */
export function coarseCountdown(ms: number): string {
    const totalMinutes = Math.max(0, Math.floor(ms / 60_000));
    const d = Math.floor(totalMinutes / 1440);
    const h = Math.floor((totalMinutes % 1440) / 60);
    const m = totalMinutes % 60;
    const pad = (n: number) => String(n).padStart(2, '0');
    if (d > 0) return `${d}d ${pad(h)}h ${pad(m)}m`;
    if (h > 0) return `${h}h ${pad(m)}m`;
    return `${m}m`;
}
